"use client";

import * as React from "react";
import { addMonths, format, isAfter, isBefore, startOfMonth } from "date-fns";
import { de, enUS, es as esLocale, fr, nl, ptBR } from "date-fns/locale";
import { CalendarDays, ChevronLeft, ChevronRight, X } from "lucide-react";
import type { Matcher } from "react-day-picker";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { DEFAULT_LOCALE, type Locale } from "@/lib/i18n";

export type SingleDate = Date | undefined;
type DateFieldLocale = Locale | "fr" | "de" | "nl";

const DATE_LOCALES = { en: enUS, es: esLocale, pt: ptBR, fr, de, nl } as const;

const DATE_CONTROL_COPY: Record<DateFieldLocale, { previousMonth: string; nextMonth: string; clearDate: string }> = {
  en: { previousMonth: "Previous month", nextMonth: "Next month", clearDate: "Clear date" },
  es: { previousMonth: "Mes anterior", nextMonth: "Mes siguiente", clearDate: "Borrar fecha" },
  pt: { previousMonth: "Mês anterior", nextMonth: "Próximo mês", clearDate: "Limpar data" },
  fr: { previousMonth: "Mois précédent", nextMonth: "Mois suivant", clearDate: "Effacer la date" },
  de: { previousMonth: "Vorheriger Monat", nextMonth: "Nächster Monat", clearDate: "Datum löschen" },
  nl: { previousMonth: "Vorige maand", nextMonth: "Volgende maand", clearDate: "Datum wissen" },
};

export function startOfTodayLocal() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

export function parseYmdLocal(ymd: string | undefined | null): Date | undefined {
  const v = (ymd ?? "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return undefined;
  const [y, m, d] = v.split("-").map((x) => Number(x));
  if (!y || !m || !d) return undefined;
  const dt = new Date(y, m - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) return undefined;
  return dt;
}

export function toYmd(date: SingleDate): string {
  return date ? format(date, "yyyy-MM-dd") : "";
}

export function DateField({
  label,
  name,
  value,
  onChange,
  onInteract,
  disabledBefore,
  preferredMonth,
  chosenDates,
  placeholder = "Pick a date",
  locale = DEFAULT_LOCALE,
  disabled = false,
}: {
  label: string;
  name: string;
  value: SingleDate;
  onChange: (next: SingleDate) => void;
  onInteract?: () => void;
  disabledBefore?: Date;
  preferredMonth?: Date;
  chosenDates?: SingleDate[];
  placeholder?: string;
  locale?: DateFieldLocale;
  disabled?: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const dateFnsLocale = DATE_LOCALES[locale];
  const controlCopy = DATE_CONTROL_COPY[locale];
  const visibleChosenDates = React.useMemo(
    () => chosenDates?.filter((date): date is Date => Boolean(date)) ?? [],
    [chosenDates],
  );

  const disabledMatchers = React.useMemo(() => {
    const out: Matcher[] = [];
    if (disabledBefore) out.push({ before: disabledBefore });
    return out.length ? out : undefined;
  }, [disabledBefore]);

  // Allow jumping up to ~18 months out; most visitors plan a few months ahead.
  const startMonth = React.useMemo(() => startOfMonth(disabledBefore ?? startOfTodayLocal()), [disabledBefore]);
  const endMonth = React.useMemo(() => {
    const base = startMonth;
    return new Date(base.getFullYear() + 1, base.getMonth() + 6, 1);
  }, [startMonth]);

  const clampMonth = React.useCallback(
    (month: Date) => {
      const normalized = startOfMonth(month);
      if (isBefore(normalized, startMonth)) return startMonth;
      if (isAfter(normalized, endMonth)) return endMonth;
      return normalized;
    },
    [endMonth, startMonth],
  );

  const [pickerMonth, setPickerMonth] = React.useState(() => clampMonth(value ?? preferredMonth ?? startMonth));

  React.useEffect(() => {
    if (open) setPickerMonth(clampMonth(value ?? preferredMonth ?? startMonth));
  }, [clampMonth, open, preferredMonth, startMonth, value]);

  const canGoPrevious = isAfter(pickerMonth, startMonth);
  const canGoNext = isBefore(pickerMonth, endMonth);

  function goToMonth(nextMonth: Date) {
    setPickerMonth(clampMonth(nextMonth));
  }

  return (
    <label className="flex min-w-0 flex-col gap-1.5">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>

      <input type="hidden" name={name} value={toYmd(value)} />

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            className={cn(
              "group h-10 w-full justify-start gap-2 border-input bg-background px-3 text-sm font-normal shadow-none",
              "hover:border-emerald/40 hover:bg-secondary hover:text-foreground",
              "data-[state=open]:border-emerald/50 data-[state=open]:ring-2 data-[state=open]:ring-ring/50",
              !value && "text-muted-foreground",
            )}
          >
            <CalendarDays className="size-4 shrink-0 text-emerald/90 group-hover:text-emerald" />
            <span className="min-w-0 flex-1 truncate text-left">
              {value ? format(value, "EEE, MMM d, yyyy", { locale: dateFnsLocale }) : placeholder}
            </span>
            {value ? (
              <span
                role="button"
                tabIndex={0}
                aria-label={controlCopy.clearDate}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (disabled) return;
                  onInteract?.();
                  onChange(undefined);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    if (disabled) return;
                    onInteract?.();
                    onChange(undefined);
                  }
                }}
                className="-mr-1 flex size-5 shrink-0 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-foreground/10 hover:text-foreground"
              >
                <X className="size-3.5" />
              </span>
            ) : null}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="center"
          className="w-[min(calc(100vw-2rem),24rem)] overflow-hidden rounded-xl border-border bg-card p-0 shadow-2xl "
        >
          <div className="border-b border-border bg-secondary p-3">
            <div className="flex items-center justify-between gap-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!canGoPrevious}
                className="h-11 min-w-11 border-border bg-background px-3"
                aria-label={controlCopy.previousMonth}
                onClick={() => goToMonth(addMonths(pickerMonth, -1))}
              >
                <ChevronLeft className="size-5" />
              </Button>
              <div className="min-w-0 text-center">
                <p className="text-sm font-semibold text-foreground">{format(pickerMonth, "MMMM yyyy", { locale: dateFnsLocale })}</p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!canGoNext}
                className="h-11 min-w-11 border-border bg-background px-3"
                aria-label={controlCopy.nextMonth}
                onClick={() => goToMonth(addMonths(pickerMonth, 1))}
              >
                <ChevronRight className="size-5" />
              </Button>
            </div>
          </div>
          <Calendar
            mode="single"
            captionLayout="label"
            startMonth={startMonth}
            endMonth={endMonth}
            month={pickerMonth}
            onMonthChange={setPickerMonth}
            locale={dateFnsLocale}
            selected={value}
            modifiers={visibleChosenDates.length ? { chosen: visibleChosenDates } : undefined}
            modifiersClassNames={{
              chosen: "ring-2 ring-emerald/55 ring-offset-2 ring-offset-card",
            }}
            onSelect={(next) => {
              onInteract?.();
              onChange(next);
              if (next) setOpen(false);
            }}
            disabled={disabledMatchers}
            className="p-4"
            classNames={{
              nav: "hidden",
              button_previous: "hidden",
              button_next: "hidden",
              month_caption: "sr-only",
              month_grid: "w-full border-collapse",
              weekdays: "flex justify-between gap-1",
              weekday: "flex h-8 w-11 items-center justify-center text-xs font-semibold uppercase text-muted-foreground",
              week: "mt-1 flex w-full justify-between gap-1",
              day: "flex w-11 justify-center p-0 text-center text-sm",
              day_button: "h-11 w-11 rounded-md p-0 text-sm font-medium hover:bg-emerald/10 hover:text-foreground",
              selected:
                "bg-emerald text-white hover:bg-emerald focus:bg-emerald",
              today: "bg-secondary text-secondary-foreground ring-1 ring-border",
              outside: "text-muted-foreground opacity-35",
              disabled: "text-muted-foreground opacity-25 line-through",
            }}
            autoFocus
          />
        </PopoverContent>
      </Popover>
    </label>
  );
}
